// Shared helpers for the recurring weekly rota (Work Allocation + Staff Daily).
//
// The rota is a weekly pattern: slots carry a weekday (0 = Monday … 6 = Sunday) and an
// "HH:mm" window, not a date. Everything here is pure so both screens agree on ordering,
// labels and shift classification.

import { Allocation, RotaSlot } from "../model/members/Rota";

export const ROTA_DAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

/** Weekday index used by the rota (0 = Monday), from a JS Date/dayjs day (0 = Sunday). */
export function toRotaDayOfWeek(jsDay: number): number {
  return (jsDay + 6) % 7;
}

/** "HH:mm" → minutes since midnight. Returns 0 for anything unparseable. */
export function toMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;
  return hours * 60 + minutes;
}

/** "17:00" → "5pm", "20:30" → "8:30pm" — the compact form the shift widgets use. */
export function formatShiftTime(time: string): string {
  const total = toMinutes(time);
  const hours24 = Math.floor(total / 60) % 24;
  const minutes = total % 60;
  const suffix = hours24 >= 12 ? "pm" : "am";
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}${minutes ? `:${String(minutes).padStart(2, "0")}` : ""}${suffix}`;
}

export function formatShiftWindow(slot: RotaSlot): string {
  return `${formatShiftTime(slot.startTime)}–${formatShiftTime(slot.endTime)}`;
}

export type ShiftType = "Opening" | "Daytime" | "Closing";

/**
 * Classifies a slot by its window so the widget can show which part of the day it covers
 * — and, later, which shift checklist applies. An early start is the opening shift; a late
 * finish is the lock-up; anything else is daytime.
 */
export function shiftTypeFor(slot: RotaSlot): ShiftType {
  const start = toMinutes(slot.startTime);
  const end = toMinutes(slot.endTime);
  // An end before the start means the shift wraps past midnight, which only a lock-up
  // does — check it first, or 22:00–02:00 falls through to "Daytime".
  if (end < start) return "Closing";
  if (start <= 9 * 60) return "Opening";
  if (end >= 20 * 60) return "Closing";
  return "Daytime";
}

/** Slots for one weekday, earliest first — the order they stack in a day column. */
export function slotsForDay(slots: RotaSlot[], dayOfWeek: number): RotaSlot[] {
  return slots
    .filter((s) => s.dayOfWeek === dayOfWeek)
    .sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime) || a.id - b.id);
}

export interface GroupedWork {
  /** Tasks and biz dev, listed individually — these are one-off and read on their own. */
  individual: Allocation[];
  /** Calls, collected behind one "Sales Calls" heading. Empty means no group is shown. */
  calls: Allocation[];
}

/**
 * Splits a shift's work into the entries shown individually and the calls shown behind one
 * collapsed heading.
 *
 * A day of assigned calls runs to dozens of rows, which buries the one-off tasks sharing the
 * widget and makes the week unreadable. Calls are the repetitive kind — same action, one per
 * lead — so they collapse into a single line carrying the count, while tasks and biz dev stay
 * visible because each is its own piece of work.
 *
 * Every call the rota receives is outstanding: the week query excludes Completed and
 * Cancelled, so the group's count is a count of calls still to make, not of calls assigned.
 */
export function groupWorkForShift(work: Allocation[]): GroupedWork {
  return {
    individual: work.filter((a) => a.kind !== "Call"),
    calls: work.filter((a) => a.kind === "Call"),
  };
}

/**
 * What to show for one call. The stored title is the generated "Call &lt;name&gt;", which
 * reads as noise inside a group already headed "Sales Calls" — so prefer the member.
 *
 * The membership id before the title matches Staff Daily's calls queue, so the same call
 * doesn't identify itself two different ways on the two screens that show it.
 */
export function callLabel(call: Allocation): string {
  return call.memberName?.trim() || call.membershipId?.trim() || call.title?.trim() || "Call";
}

/**
 * What to show for one allocation row. Business Development has no title — it's a focus area and an
 * optional target — so it reads as "Corporates → Local offices".
 *
 * Shared with the row's accessible name so the two can't describe different things: the
 * Remove control is icon-only, and "Remove" alone tells a screen-reader user nothing about
 * which of the shift's rows they're on.
 */
export function allocationLabel(a: Allocation): string {
  if (a.kind === "BizDev") {
    const focus = a.focusArea?.trim() || "Business Development";
    const target = a.target?.trim();
    return target ? `${focus} → ${target}` : focus;
  }
  if (a.kind === "Call") return callLabel(a);
  return a.title?.trim() || "Task";
}
